import React, {CSSProperties, MouseEvent, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TShow} from '../../../../hooks/useShow';
import {TEticketSeat, TEticketSeatMap} from '../../../../redux/eTicket/eTicket.type';
import SheetShell from '../SheetShell/SheetShell';
import TicketSeatTiles from '../TicketSeatTiles/TicketSeatTiles';
import SeatGrid from '../SeatGrid/SeatGrid';
import VenueLine from '../VenueLine/VenueLine';
import sanitizeHtml from '../../../../library/sanitizeHtml';
import {generateSVG} from '../../../../functions/generateSVG';
import * as css from './SeatMapSheet.css';

interface SeatMapSheetProps {
    show: TShow;
    venue?: string;
    seat?: TEticketSeat;
    design?: TEticketSeatMap;
}

const TIP_EDGE_GAP = 8;
const TIP_ARROW_INSET = 12;

function SeatMapSheet({show, venue, seat, design}: SeatMapSheetProps): React.ReactElement {
    const {t} = useTranslation('eTicket');
    const [zoomedAid, setZoomedAid] = useState<string>();
    const [tipAnchor, setTipAnchor] = useState<{x: number; y: number}>();
    const [tipPos, setTipPos] = useState<{left: number; arrow: number}>();
    const mapWrapRef = useRef<HTMLDivElement>(null);
    const tipRef = useRef<HTMLDivElement>(null);
    const myAid = seat?.aid != null ? String(seat.aid) : undefined;

    // The sheet stays mounted while closed, so reset back to the block overview on close —
    // otherwise re-opening jumps straight to the seat grid.
    if(!show.isOpen && zoomedAid) {
        setZoomedAid(undefined);
    }

    // Nudge the buyer to tap their (yellow) block: a tooltip pops in over its middle after a beat, then fades.
    useEffect(() => {
        if(!show.isOpen || zoomedAid) {
            setTipAnchor(undefined);
            return;
        }
        const showTimer = setTimeout(() => {
            const block = mapWrapRef.current?.querySelector('[data-mine]');
            if(!block || !mapWrapRef.current) {
                return;
            }
            const blockRect = block.getBoundingClientRect();
            const wrapRect = mapWrapRef.current.getBoundingClientRect();
            setTipAnchor({
                x: blockRect.left - wrapRect.left + blockRect.width / 2,
                y: blockRect.top - wrapRect.top + blockRect.height / 2
            });
        }, 900);
        const hideTimer = setTimeout(() => setTipAnchor(undefined), 7000);
        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [show.isOpen, zoomedAid]);

    // The bubble is nowrap and is often wider than its block, so a block near an edge would push it
    // past the sheet, which clips it (SheetShell's panel is `overflow: hidden`). Keep it inside the
    // map and slide the arrow within it so it still marks the block once the bubble is held back.
    useLayoutEffect(() => {
        if(!tipAnchor || !tipRef.current || !mapWrapRef.current) {
            setTipPos(undefined);
            return;
        }
        const half = tipRef.current.offsetWidth / 2;
        const min = half + TIP_EDGE_GAP;
        const max = Math.max(min, mapWrapRef.current.clientWidth - half - TIP_EDGE_GAP);
        const left = Math.min(Math.max(tipAnchor.x, min), max);
        const arrowLimit = Math.max(0, half - TIP_ARROW_INSET);
        setTipPos({left, arrow: Math.min(Math.max(tipAnchor.x - left, -arrowLimit), arrowLimit)});
    }, [tipAnchor]);

    // Highlight only the block of THIS ticket (not every block bought in the order).
    const svg = useMemo(() => {
        if(!design) {
            return null;
        }
        const area = design.area && myAid
            ? Object.fromEntries(Object.entries(design.area).map(([aid, item]) => [aid, {...item, mine: aid === myAid}]))
            : design.area;
        return generateSVG({...design, area});
    }, [design, myAid]);

    const zoomedSeats = zoomedAid ? design?.area?.[zoomedAid]?.seats : undefined;

    const onMapClick = (event: MouseEvent<HTMLDivElement>) => {
        const aid = (event.target as Element).closest('[data-mine]')?.getAttribute('data-aid');
        if(aid && design?.area?.[aid]?.seats?.length) {
            setZoomedAid(aid);
        }
    };

    return (
        <SheetShell show={show} title={t('seatMapTitle')} data-cy="seat-map-sheet">
            <css.body>
                {zoomedSeats?.length ? (
                    <SeatGrid seats={zoomedSeats} mySeat={seat} onBack={() => setZoomedAid(undefined)} />
                ) : (
                    <>
                        <VenueLine name={venue} />
                        <TicketSeatTiles seat={seat} />
                        {svg && (
                            <css.mapWrap ref={mapWrapRef}>
                                <css.map data-cy="seat-map-svg" onClick={onMapClick} dangerouslySetInnerHTML={sanitizeHtml(svg)} />
                                {tipAnchor && (
                                    <css.tip
                                        ref={tipRef}
                                        data-cy="seat-map-tip"
                                        style={{
                                            left: tipPos?.left ?? tipAnchor.x,
                                            top: tipAnchor.y,
                                            visibility: tipPos ? 'visible' : 'hidden',
                                            '--tip-arrow': `${tipPos?.arrow ?? 0}px`
                                        } as CSSProperties}
                                    >
                                        {t('blockTapHint')}
                                    </css.tip>
                                )}
                            </css.mapWrap>
                        )}
                    </>
                )}
            </css.body>
        </SheetShell>
    );
}

export default SeatMapSheet;
